import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';

import {PermissionService} from '../../../../services/permission/permission.service';
import {ScrollingModule} from '@angular/cdk/scrolling';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  faArrowDown,
  faArrowUp,
  faChevronDown,
  faChevronRight,
  faCircleCheck,
  faCircleInfo,
  faCopy,
  faEuroSign,
  faFile,
  faSquareTerminal,
} from '@fortawesome/pro-solid-svg-icons';
import {BicBadgeComponent, TooltipDirective} from '@buildinginformationcloud/bic-ui-components';
import {
  ExecutionStep,
  LogEntry,
  ProgressInfo,
  RulesetRunStatus,
  SelectedFileSummary,
  SelectedRuleSummary,
} from './step-log.model';
import {BicFilterComponent, BicFilterConfig} from '../../../util/design/bic-filter/bic-filter.component';
import {CheckDurations, DurationBucket, DurationSource} from '../../../../services/workflow.service';

/**
 * Anzeigegrenze für einen einzelnen Detailwert.
 *
 * Ein Modul-Output kann mehrere Megabyte groß sein. Ihn ungekürzt in ein `<span>` zu schreiben
 * blockiert Layout und Paint des Browsers – unabhängig von Angular. Wer den vollen Inhalt braucht,
 * findet ihn im Prüfbericht bzw. in `workflow_step_log.output_data_json`.
 */
export const DETAIL_VALUE_MAX_CHARS = 2000;

/** Kürzt einen Detailwert auf Anzeigelänge und markiert die Kürzung sichtbar. */
export function truncateDetailValue(key: string, value: string): LogDetailRow {
  if (value.length <= DETAIL_VALUE_MAX_CHARS) {
    return {key, value};
  }
  return {
    key,
    value: `${value.slice(0, DETAIL_VALUE_MAX_CHARS)} … (${value.length.toLocaleString('de-DE')} Zeichen, gekürzt)`,
    truncated: true,
  };
}

/** Eine Schlüssel/Wert-Zeile im aufgeklappten Detailblock einer Protokollzeile. */
export interface LogDetailRow {
  key: string;
  value: string;
  /** Wert war länger als die Anzeigegrenze und ist gekürzt. */
  truncated?: boolean;
}

/**
 * Der Zustand des gerade aufgeklappten Protokolleintrags.
 *
 * Bewusst genau EINER: Vorher konnte jede Zeile gleichzeitig offen sein, und jede offene Zeile
 * rendert den Modul-Output. Bei einem großen Prüfbericht ist das ein Text-Node von mehreren
 * Megabyte, der Layout und Paint blockiert – auch ohne Angular.
 */
export interface LogDetailState {
  stepLogId: string;
  state: 'loading' | 'ready' | 'error';
  rows: LogDetailRow[];
  message?: string;
}

/** Eine gerenderte Protokollzeile – alles vorberechnet, damit das Template nur noch liest. */
interface LogRowVm {
  log: LogEntry;
  key: string;
  message: string;
  levelLabel: string;
  levelType: 'success' | 'warning' | 'error' | 'info';
  relativeTimestamp: string;
  absoluteTimestamp: string;
  hasDetails: boolean;
  stepLogId?: string;
}

/** Eine Zeile in den Panels "Ausgeführte Prüfregeln" / "Geprüfte Dateien". */
interface DurationCellVm {
  durationText: string;
  /** Tooltip-Zusatz, der die Herkunft der Zahl benennt (gemessen vs. inkl. Wartezeit). */
  durationHint: string;
}

@Component({
  selector: 'my-checks-step-log',
  standalone: true,
  imports: [FaIconComponent, BicBadgeComponent, BicFilterComponent, TooltipDirective, ScrollingModule],
  templateUrl: './step-log.component.html',
  styleUrls: ['./step-log.component.scss'],
  host: {class: 'flex flex-col h-full w-full overflow-hidden'},
  // OnPush: Diese Komponente stellt nur dar, und alle Eingaben werden ersetzt statt verändert. Ohne
  // OnPush löst jede Mausbewegung über der Ereignisliste (Tooltips, group-hover) einen vollen
  // Change-Detection-Zyklus über hunderte Zeilen aus – das war der zweite Teil des Einfrierens.
  // Die einzige lokal veränderliche Größe ist der aufgeklappte Eintrag; der markiert selbst.
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepLogComponent implements OnChanges {
  private permissionService = inject(PermissionService);

  @Input() steps: ExecutionStep[] = [];
  @Input() logs: LogEntry[] = [];
  @Input() progress: ProgressInfo = {
    currentStep: 0,
    totalSteps: 0,
    elapsedSeconds: 0,
    percentage: 0,
    checkName: 'Prüfung',
  };
  /** Selected rule summaries passed from the execution component. */
  @Input() selectedRules: SelectedRuleSummary[] = [];
  /** Selected file summaries passed from the execution component. */
  @Input() selectedFiles: SelectedFileSummary[] = [];
  /**
   * Serverseitig aggregierte Prüfdauern je Prüfregel und je Datei.
   *
   * Ersetzt die frühere clientseitige Rechnung aus `instance.steps`, die für HYBRID und gemischte
   * Läufe strukturell nichts finden konnte (die tragenden Schritte liegen dort in Kind- und
   * Enkel-Instanzen). `null`, solange nichts geladen ist oder der Abruf fehlschlug – dann zeigen die
   * Spalten "–".
   */
  @Input() durations: CheckDurations | null = null;

  /**
   * Zustand des aufgeklappten Protokolleintrags. Die Details lädt der Elternteil auf
   * {@link detailRequested} hin nach, statt sie an jeder Zeile mitzuschleppen.
   */
  @Input() activeDetail: LogDetailState | null = null;

  /** Der Nutzer möchte die Details dieser Zeile sehen. */
  @Output() detailRequested = new EventEmitter<LogEntry>();
  /** Der Nutzer hat den offenen Detailblock geschlossen. */
  @Output() detailClosed = new EventEmitter<void>();
  /** Current status message (e.g. loading message from the execution step). */
  @Input() statusMessage = '';

  activeEventFilters: string[] = [];
  eventFilterConfig: BicFilterConfig[] = [
    {
      key: 'level',
      displayName: 'Level',
      type: 'text',
      options: ['INFO', 'WARNING', 'ERROR'],
      formatOption: (value: string) => this.formatFilterLevel(value),
    },
  ];

  protected readonly faFile = faFile;
  protected readonly faCopy = faCopy;
  protected readonly faSquareTerminal = faSquareTerminal;
  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faCircleInfo = faCircleInfo;
  protected readonly faArrowUp = faArrowUp;
  protected readonly faArrowDown = faArrowDown;
  protected readonly faEuroSign = faEuroSign;
  protected readonly faChevronDown = faChevronDown;
  protected readonly faChevronRight = faChevronRight;

  /**
   * Identitätsbasierter Memo-Speicher für die vom Template gelesenen Ableitungen – dasselbe Muster
   * wie in `check-report-page.component.ts`.
   *
   * Vorher waren `filteredLogs`, `rulesPanelGroups`, `filesPanelRows` und `aiUsageSummaryBadge`
   * Getter, die das Template mehrfach pro Change-Detection-Zyklus aufrief. Jeder Aufruf lief über
   * alle Log-Einträge und gab ein NEUES Array zurück – `*cdkVirtualFor` sah damit in jedem Zyklus
   * einen neuen Input und ließ seinen Differ über die gesamte Liste laufen.
   *
   * Schlüssel sind Objekt-Identitäten: Alle Inputs dieser Komponente werden ersetzt, nie verändert.
   */
  private memo = new Map<string, { deps: ReadonlyArray<unknown>; value: unknown }>();

  /** Zählt lokale Zustandswechsel (Filter, aufgeklappte Zeile) für die Memo-Schlüssel mit. */
  private localRevision = 0;
  /** Schlüssel der aufgeklappten Zeile; `null`, wenn keine offen ist. */
  private expandedKey: string | null = null;
  /**
   * Detailzeilen eines SSE-Eintrags, der seine Details selbst mitbringt. Nur für die EINE offene
   * Zeile berechnet – vorher passierte dasselbe für jede sichtbare Zeile in jedem CD-Zyklus.
   */
  private inlineDetailRows: LogDetailRow[] = [];

  /**
   * KI-Modellpreise in USD pro 1 Mio. Tokens (Stand: platform.openai.com/docs/pricing bzw.
   * platform.claude.com/docs/en/about-claude/pricing). Wird zur Schätzung der Check-Kosten aus
   * den geloggten Input-/Output-Tokens genutzt. Modelle, die hier nicht gelistet sind, fließen
   * nicht in die Kostenschätzung ein.
   */
  private readonly aiModelPricingPerMillionTokens: Record<string, { input: number; output: number }> = {
    // OpenAI
    'gpt-5.6-sol': {input: 5.00, output: 30.00},
    'gpt-5.6-terra': {input: 2.00, output: 12.00},
    'gpt-5.6-luna': {input: 0.20, output: 1.20},
    'gpt-5.5': {input: 5.00, output: 30.00},
    'gpt-5.5-pro': {input: 30.00, output: 180.00},
    'gpt-5.4': {input: 2.50, output: 15.00},
    'gpt-5.4-mini': {input: 0.75, output: 4.50},
    'gpt-5.4-nano': {input: 0.20, output: 1.25},
    'gpt-5.4-pro': {input: 30.00, output: 180.00},
    'gpt-5.2': {input: 1.75, output: 14.00},
    'gpt-5.2-pro': {input: 21.00, output: 168.00},
    'gpt-5.1': {input: 1.25, output: 10.00},
    'gpt-5': {input: 1.25, output: 10.00},
    'gpt-5-mini': {input: 0.25, output: 2.00},
    'gpt-5-nano': {input: 0.05, output: 0.40},
    'gpt-5-pro': {input: 15.00, output: 120.00},
    'gpt-4.1': {input: 2.00, output: 8.00},
    'gpt-4.1-mini': {input: 0.40, output: 1.60},
    'gpt-4.1-nano': {input: 0.10, output: 0.40},
    'gpt-4o': {input: 2.50, output: 10.00},
    'gpt-4o-mini': {input: 0.15, output: 0.60},
    'o1': {input: 15.00, output: 60.00},
    'o1-pro': {input: 150.00, output: 600.00},
    'o3-pro': {input: 20.00, output: 80.00},
    'o3': {input: 2.00, output: 8.00},
    'o3-mini': {input: 1.10, output: 4.40},
    'o4-mini': {input: 1.10, output: 4.40},
    'gpt-3.5-turbo': {input: 0.50, output: 1.50},
    // Anthropic (Claude)
    'claude-fable-5': {input: 10.00, output: 50.00},
    'claude-mythos-5': {input: 10.00, output: 50.00},
    'claude-opus-5': {input: 5.00, output: 25.00},
    'claude-opus-4-8': {input: 5.00, output: 25.00},
    'claude-opus-4-7': {input: 5.00, output: 25.00},
    'claude-opus-4-6': {input: 5.00, output: 25.00},
    'claude-opus-4-5': {input: 5.00, output: 25.00},
    'claude-opus-4-1': {input: 15.00, output: 75.00},
    'claude-opus-4': {input: 15.00, output: 75.00},
    // Sonnet 5: Einführungspreis $2/$10 gilt bis 31.08.2026, danach $3/$15.
    'claude-sonnet-5': {input: 2.00, output: 10.00},
    'claude-sonnet-4-6': {input: 3.00, output: 15.00},
    'claude-sonnet-4-5': {input: 3.00, output: 15.00},
    'claude-sonnet-4': {input: 3.00, output: 15.00},
    'claude-haiku-4-5': {input: 1.00, output: 5.00},
    'claude-haiku-3-5': {input: 0.80, output: 4.00},
  };

  get rulesPanelGroups(): Array<{
    rulesetName: string;
    rulesCountText: string;
    rulesetType: string;
    rules: Array<{
      ruleId: string;
      statusText: string;
      statusType: 'success' | 'warning' | 'error' | 'info';
      durationText: string;
      durationHint: string;
    }>;
  }> {
    return this.cached('rulesPanelGroups', [this.selectedRules, this.durations, this.fileCount], () =>
      this.selectedRules.map(ruleset => {
        const ruleNames = ruleset.ruleNames?.length ? ruleset.ruleNames : [ruleset.name];
        // Positionsgleiche IDs nur nutzen, wenn die Listen wirklich gleich lang sind – siehe
        // SelectedRuleSummary.ruleIds.
        const ruleIds = ruleset.ruleIds?.length === ruleNames.length ? ruleset.ruleIds : undefined;
        // Bei gemischten Läufen hat jedes Regelset seinen eigenen Teillauf-Status; sonst fällt es
        // auf den Gesamtstatus zurück (Verhalten wie bisher).
        const statusText = this.rulesetStatusText(ruleset.runStatus);
        const statusType = this.rulesetStatusType(ruleset.runStatus);
        const rulesInSet = this.rulesInSet(ruleset);
        return {
          rulesetName: ruleset.name,
          rulesCountText: `${rulesInSet} ${rulesInSet === 1 ? 'Regel' : 'Regeln'}`,
          rulesetType: ruleset.type || 'IDS',
          rules: ruleNames.map((ruleName, index) => {
            const bucket = this.findBucket(this.durations?.rules, ruleIds?.[index], ruleName);
            return {
              ruleId: ruleName,
              statusText,
              statusType,
              ...this.durationCell(bucket),
            };
          }),
        };
      }));
  }
  trackRule = (_: number, rule: { ruleId: string }): string => rule.ruleId;
  trackFile = (_: number, file: { fileName: string }): string => file.fileName;

  get filesPanelRows(): Array<{
    fileName: string;
    fileType: string;
    statusText: string;
    statusType: 'success' | 'warning' | 'error' | 'info';
    durationText: string;
    durationHint: string;
  }> {
    return this.cached('filesPanelRows',
      [this.selectedFiles, this.durations, this.rowStatusText, this.rowStatusType], () =>
        this.selectedFiles.map(file => ({
          fileName: file.name,
          fileType: this.getFileExtensionTag(file),
          statusText: this.rowStatusText,
          statusType: this.rowStatusType,
          ...this.durationCell(this.findBucket(this.durations?.files, file.fileId, file.name)),
        })));
  }

  /**
   * Die gerenderten Protokollzeilen – gefiltert, umgekehrt und vollständig vorformatiert.
   *
   * Level-Badge und beide Zeitangaben entstehen hier einmal je Eintrag. Vorher rief das Template
   * `formatAbsoluteTimestamp()` und `formatRelativeTimestamp()` pro sichtbarer Zeile pro
   * Change-Detection-Zyklus auf – jeweils mit `new Date()` und `toLocaleTimeString()`.
   */
  get logRows(): LogRowVm[] {
    return this.cached('logRows',
      [this.logs, this.activeEventFilters, this.progress?.startedAtIso, this.localRevision], () => {
        // this.logs kommt chronologisch aufsteigend (ältester Eintrag zuerst) an - für die Anzeige
        // sollen die neuesten Ereignisse oben stehen, daher hier umkehren statt an der Quelle.
        const expected = this.activeEventFilters.length > 0
          ? new Set(this.activeEventFilters)
          : null;
        const rows: LogRowVm[] = [];
        for (let i = this.logs.length - 1; i >= 0; i--) {
          const log = this.logs[i];
          if (expected && !expected.has(this.normalizeLogLevel(log.level))) continue;
          rows.push({
            log,
            key: log.stepLogId ?? `${i}:${log.occurredAtIso ?? log.timestamp}:${log.message}`,
            message: log.message,
            levelLabel: this.eventLevelLabel(log.level),
            levelType: this.eventLevelType(log.level),
            relativeTimestamp: this.formatRelativeTimestamp(log),
            absoluteTimestamp: this.formatAbsoluteTimestamp(log),
            // Kein Aufbau der Detailliste, nur die Frage OB es welche gibt. Genau hier lag der
            // Einfrierer: hasDetails() baute die Liste inklusive JSON.stringify über den ganzen
            // Modul-Output - drei- bis viermal pro sichtbarer Zeile pro Zyklus.
            hasDetails: this.rowHasDetails(log),
            stepLogId: log.stepLogId,
          });
        }
        return rows;
      });
  }

  get progressPercentage(): number {
    return Math.min(100, Math.max(0, this.progress.percentage));
  }

  get elapsedFormatted(): string {
    if (!this.progress.startedAtIso || !this.progress.endedAtIso) {
      return '–';
    }
    const startedAt = new Date(this.progress.startedAtIso).getTime();
    const endedAt = new Date(this.progress.endedAtIso).getTime();
    if (Number.isNaN(startedAt) || Number.isNaN(endedAt) || endedAt < startedAt) {
      return '–';
    }
    const durationSeconds = Math.floor((endedAt - startedAt) / 1000);
    const hours = Math.floor(durationSeconds / 3600);
    const mins = Math.floor((durationSeconds % 3600) / 60);
    const secs = durationSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  get rulesetCount(): number {
    return this.selectedRules.length;
  }

  get ruleCount(): number {
    return this.selectedRules.reduce((sum, ruleset) => sum + this.rulesInSet(ruleset), 0);
  }

  /**
   * Regelanzahl eines Regelsets: die Einzelnamen, sonst die mitgelieferte Anzahl.
   *
   * Die Namen fehlen, wenn die Auswahl nach einem Reload aus dem Instanz-State rekonstruiert wurde
   * und dort ein ganzes IDS-/HYBRID-Set als eine Pseudo-Regel steht – dann traegt
   * {@link SelectedRuleSummary.ruleCount} die echte Zahl.
   */
  private rulesInSet(ruleset: SelectedRuleSummary): number {
    return ruleset.ruleNames?.length ?? ruleset.ruleCount ?? 1;
  }

  get fileCount(): number {
    return this.selectedFiles.length;
  }

  get totalFileSizeFormatted(): string {
    const totalBytes = this.selectedFiles.reduce((sum, f) => sum + (f.size ?? 0), 0);
    return this.formatBytes(totalBytes);
  }

  get filesSummaryText(): string {
    return `${this.fileCount} Dateien - ${this.totalFileSizeFormatted}`;
  }

  get rulesSummaryText(): string {
    return `${this.rulesetCount} myChecks und ${this.ruleCount} Prüfregeln`;
  }

  /** Anzahl der aktuell sichtbaren Protokollzeilen (für die Kopfzeile). */
  get filteredLogCount(): number {
    return this.logRows.length;
  }

  /**
   * Aggregiertes KI-Token-Badge (Pfeil hoch = Input, Pfeil runter = Output) über alle
   * Log-Einträge des Prüflaufs, das absolut im Header oben rechts neben "myCheck Version"
   * angezeigt wird. Gibt null zurück, wenn keiner der Log-Einträge Token-Daten enthält.
   */
  get aiUsageSummaryBadge(): { models: string; inputText: string; outputText: string; costText?: string } | null {
    return this.cached('aiUsageSummaryBadge', [this.logs], () => this.computeAiUsageSummaryBadge());
  }

  /** Die anzuzeigenden Detailzeilen: nachgeladen, wenn möglich, sonst die mitgelieferten. */
  get visibleDetailRows(): LogDetailRow[] {
    if (!this.canViewLogDetails()) return [];
    if (this.activeDetail?.state === 'ready') return this.activeDetail.rows;
    return this.inlineDetailRows;
  }

  get detailIsLoading(): boolean {
    if (!this.canViewLogDetails()) return false;
    return this.activeDetail?.state === 'loading';
  }

  get detailErrorMessage(): string | undefined {
    if (!this.canViewLogDetails()) return undefined;
    return this.activeDetail?.state === 'error'
      ? (this.activeDetail.message ?? 'Details konnten nicht geladen werden.')
      : undefined;
  }

  private normalizeDurationKey(value: string): string {
    return value.trim().toLocaleLowerCase('de-DE');
  }

  /** Kurzes Extension-Tag (max. 4 Zeichen, Großbuchstaben) aus dem Dateinamen, sonst Fallback auf den Dateityp. */
  private getFileExtensionTag(file: SelectedFileSummary): string {
    const match = /\.([a-z0-9]+)$/i.exec(file.name ?? '');
    const extension = match ? match[1] : (file.fileType || 'OTHER');
    return extension.toUpperCase().slice(0, 4);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Ein ersetzter Input macht jede Ableitung ungültig. Das Memo prüft die Identitäten ohnehin
    // selbst; das Leeren hält die Map klein, statt sie über die Lebensdauer wachsen zu lassen.
    if (Object.keys(changes).length > 0) {
      this.memo.clear();
    }
  }

  /**
   * trackBy-Funktionen. Seit die Ableitungen memoisiert sind (siehe {@link memo}) liefern sie
   * zwischen zwei Change-Detection-Zyklen dieselben Objekte, sodass *ngFor gar nicht mehr
   * neu aufbaut. trackBy bleibt trotzdem: Es hält die Zeilen auch dann stabil, wenn sich ein Input
   * tatsächlich ändert – und damit die auf ihnen liegende TooltipDirective, die sonst beim
   * Neuaufbau sofort wieder zerstört wird (Tooltip blitzt weg).
   */
  trackRuleset = (_: number, group: { rulesetName: string }): string => group.rulesetName;

  trackLogRow = (_: number, row: LogRowVm): string => row.key;

  trackDetailRow = (_: number, row: LogDetailRow): string => row.key;

  /** Der offene Detailblock gehört zu dieser Zeile. */
  isRowExpanded(row: LogRowVm): boolean {
    return !!this.expandedKey && this.expandedKey === row.key;
  }

  /**
   * Statischer USD/EUR-Umrechnungskurs für die Kostenschätzung (kein Live-Wechselkurs, da die
   * Preistabelle selbst nur eine Schätzung ist). Bei Bedarf hier anpassen.
   */
  private readonly usdToEurRate = 0.92;

  /**
   * Schätzt die Kosten in USD anhand der bekannten Modellpreise. Modelle ohne Preiseintrag
   * (z.B. unbekannte/zukünftige Modellnamen) werden von der Schätzung ausgeschlossen, statt
   * einen falschen Wert vorzutäuschen.
   */
  private estimateCostUsd(tokensByModel: Map<string, { input: number; output: number }>): number | undefined {
    let costSum = 0;
    let hasPricedModel = false;

    for (const [model, tokens] of tokensByModel) {
      const pricing = this.resolveModelPricing(model);
      if (!pricing) continue;
      hasPricedModel = true;
      costSum += (tokens.input / 1_000_000) * pricing.input + (tokens.output / 1_000_000) * pricing.output;
    }

    return hasPricedModel ? costSum : undefined;
  }

  /** Sucht den Modellpreis per Exact-Match, sonst per längstem Präfix (z.B. Deployment-Suffixe wie Datumsstempel). */
  private resolveModelPricing(model: string): { input: number; output: number } | undefined {
    const normalized = model.trim().toLowerCase();
    if (this.aiModelPricingPerMillionTokens[normalized]) return this.aiModelPricingPerMillionTokens[normalized];
    const matchingKey = Object.keys(this.aiModelPricingPerMillionTokens)
      .sort((a, b) => b.length - a.length)
      .find(key => normalized.startsWith(key));
    return matchingKey ? this.aiModelPricingPerMillionTokens[matchingKey] : undefined;
  }

  /**
   * Formatiert einen USD-Betrag als EUR-Zahl (de-DE), mit mehr Nachkommastellen bei
   * Cent-Bruchteilen. Ohne Währungssymbol, da das €-Icon im Badge die Einheit bereits anzeigt.
   */
  private formatCost(usdValue: number | undefined): string | undefined {
    if (usdValue == null || !Number.isFinite(usdValue)) return undefined;
    const eurValue = usdValue * this.usdToEurRate;
    const decimals = eurValue > 0 && eurValue < 0.01 ? 4 : 2;
    return eurValue.toLocaleString('de-DE', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  get overallStatusType(): 'success' | 'warning' | 'error' | 'info' {
    switch (this.progress.status) {
      case 'COMPLETED':
        return 'success';
      case 'FAILED':
      case 'CANCELLED':
        return 'error';
      case 'PAUSED':
      case 'PENDING':
        return 'warning';
      default:
        return 'info';
    }
  }

  get overallStatusText(): string {
    switch (this.progress.status) {
      case 'COMPLETED':
        return 'Abgeschlossen';
      case 'FAILED':
        return 'Fehlgeschlagen';
      case 'CANCELLED':
        return 'Abgebrochen';
      case 'PAUSED':
        return 'Pausiert';
      case 'PENDING':
        return 'Wartend';
      default:
        return 'Läuft';
    }
  }

  get startText(): string {
    if (this.progress.startedAtIso) {
      const value = this.toDateTimeDisplay(this.progress.startedAtIso);
      if (value) return value;
    }
    return this.progress.startedAt || '–';
  }

  get endText(): string {
    if (this.progress.endedAtIso) {
      const value = this.toDateTimeDisplay(this.progress.endedAtIso);
      if (value) return value;
    }
    return this.progress.endedAt || '–';
  }

  get rowStatusType(): 'success' | 'warning' | 'error' | 'info' {
    if (this.progress.status === 'COMPLETED') return 'success';
    if (this.progress.status === 'FAILED' || this.progress.status === 'CANCELLED') return 'error';
    return 'info';
  }

  get rowStatusText(): string {
    if (this.progress.status === 'COMPLETED') return 'Abgeschlossen';
    if (this.progress.status === 'FAILED' || this.progress.status === 'CANCELLED') return 'Fehlgeschlagen';
    return 'In Bearbeitung';
  }

  /**
   * Status eines einzelnen Regelsets. Bei gemischten Läufen (ein Teillauf je Regelset) trägt jedes
   * Regelset seinen eigenen Status; sonst gilt der Gesamtstatus des Laufs.
   */
  private rulesetStatusType(runStatus?: RulesetRunStatus): 'success' | 'warning' | 'error' | 'info' {
    switch (runStatus) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'running':
      case 'pending':
        return 'info';
      default:
        return this.rowStatusType;
    }
  }

  private rulesetStatusText(runStatus?: RulesetRunStatus): string {
    switch (runStatus) {
      case 'completed':
        return 'Abgeschlossen';
      case 'failed':
        return 'Fehlgeschlagen';
      case 'running':
        return 'In Bearbeitung';
      case 'pending':
        return 'Wartet';
      default:
        return this.rowStatusText;
    }
  }

  copyCheckId() {
    const checkId = this.progress.checkId;
    if (!checkId || !navigator?.clipboard?.writeText) return;
    navigator.clipboard.writeText(checkId);
  }

  onEventFiltersChange(event: { [key: string]: any[] }) {
    this.activeEventFilters = (event['level'] ?? []).map(level => String(level).toUpperCase());
  }

  formatFilterLevel(level: string): string {
    const normalized = this.normalizeLogLevel(level);
    if (normalized === 'ERROR') return 'Fehler';
    if (normalized === 'WARNING') return 'Warnung';
    return 'Info';
  }

  eventLevelType(level: string): 'success' | 'warning' | 'error' | 'info' {
    const normalized = this.normalizeLogLevel(level);
    if (normalized === 'ERROR') return 'error';
    if (normalized === 'WARNING') return 'warning';
    return 'info';
  }

  eventLevelLabel(level: string): string {
    const normalized = this.normalizeLogLevel(level);
    if (normalized === 'ERROR') return 'Fehler';
    if (normalized === 'WARNING') return 'Warnung';
    return 'Info';
  }

  eventModuleLabel(log: LogEntry): string {
    return log.moduleId || 'allgemein';
  }

  eventPhaseLabel(log: LogEntry): string {
    if (!log.phase) return 'info';
    const normalized = log.phase.toLowerCase();
    if (normalized === 'start') return 'Start';
    if (normalized === 'complete') return 'Abschluss';
    if (normalized === 'fallback') return 'Fallback';
    if (normalized === 'error') return 'Fehler';
    return log.phase;
  }

  /**
   * Klappt eine Zeile auf bzw. zu. Es ist immer höchstens EINE offen – siehe {@link LogDetailState}.
   *
   * Zeilen mit StepLog-ID lassen ihre Details nachladen; SSE-Einträge tragen sie schon bei sich und
   * werden direkt gerendert.
   */
  toggleRowDetails(row: LogRowVm): void {
    if (!this.canViewLogDetails()) return;
    if (!row.hasDetails) return;
    if (this.isRowExpanded(row)) {
      this.expandedKey = null;
      this.inlineDetailRows = [];
      this.localRevision++;
      this.detailClosed.emit();
      return;
    }
    this.expandedKey = row.key;
    this.inlineDetailRows = row.stepLogId ? [] : this.buildInlineDetailRows(row.log);
    this.localRevision++;
    if (row.stepLogId) {
      this.detailRequested.emit(row.log);
    }
  }

  private cached<T>(key: string, deps: ReadonlyArray<unknown>, compute: () => T): T {
    const hit = this.memo.get(key);
    if (hit && hit.deps.length === deps.length && hit.deps.every((dep, i) => dep === deps[i])) {
      return hit.value as T;
    }
    const value = compute();
    this.memo.set(key, {deps, value});
    return value;
  }

  /**
   * Sucht den Dauer-Bucket zu einer Regel bzw. Datei: erst über die stabile ID, dann über den Namen.
   *
   * Der Namensvergleich ist kein Komfort, sondern für IDS die einzige Möglichkeit – dort trägt der
   * Modul-Output keine Spezifikations-ID (siehe `IdsDurationExtractor` im Backend). Normalisiert,
   * weil Name aus der Regelauswahl und Name aus der Modul-Ausgabe sich in Groß-/Kleinschreibung und
   * Rand-Leerzeichen unterscheiden können.
   */
  private findBucket(buckets: DurationBucket[] | undefined,
                     id: string | undefined,
                     name: string | undefined): DurationBucket | undefined {
    if (!buckets?.length) return undefined;
    if (id) {
      const byId = buckets.find(bucket => bucket.id === id);
      if (byId) return byId;
    }
    if (!name) return undefined;
    const normalized = this.normalizeDurationKey(name);
    return buckets.find(bucket => !!bucket.name && this.normalizeDurationKey(bucket.name) === normalized);
  }

  /**
   * Baut Anzeigetext und Tooltip einer Dauer-Zelle.
   *
   * Angezeigt wird der DURCHSCHNITT je Messpunkt, nicht die Summe: Die Dauer einer Prüfregel ist über
   * alle Dateien kumuliert (und umgekehrt). `sampleCount` kommt dafür aus dem Backend – vorher teilte
   * das Frontend durch die Zahl der ausgewählten Dateien bzw. Regeln, was bei Teilausführungen und
   * im COMBINED-Modus die falsche Bezugsgröße war.
   */
  private durationCell(bucket: DurationBucket | undefined): DurationCellVm {
    if (!bucket || bucket.durationMs == null || bucket.sampleCount <= 0) {
      return {durationText: '–', durationHint: 'Keine Prüfdauer für diese Zeile gemessen.'};
    }
    const average = bucket.durationMs / bucket.sampleCount;
    return {
      durationText: this.formatDurationMs(average),
      durationHint: this.durationHint(bucket),
    };
  }

  /** Benennt Bezugsgröße, Herkunft und Vollständigkeit – damit die Zahl nicht mehr verspricht als sie ist. */
  private durationHint(bucket: DurationBucket): string {
    const parts: string[] = [];
    parts.push(bucket.sampleCount === 1
      ? 'Eine Messung'
      : `Durchschnitt aus ${bucket.sampleCount} Messungen`);
    if (bucket.combined) {
      parts.push('gemeinsame Prüfung aller Dateien (keine Aufteilung je Datei)');
    }
    const origin = this.durationSourceLabel(bucket.source);
    if (origin) parts.push(origin);
    if (bucket.missingCount > 0) {
      parts.push(`${bucket.missingCount} Schritt(e) ohne Messwert – Summe unvollständig`);
    }
    return parts.join(' · ');
  }

  private durationSourceLabel(source: DurationSource | undefined): string | undefined {
    switch (source) {
      case 'MODULE_REPORTED':
        return 'reine Bearbeitungszeit';
      case 'STEP_CLOCK':
        return 'inklusive Wartezeit in der Warteschlange';
      case 'INSTANCE_CLOCK':
        return 'Laufzeit des gesamten Teillaufs';
      case 'MIXED':
        return 'gemischte Messquellen';
      default:
        return undefined;
    }
  }

  private computeAiUsageSummaryBadge(): {
    models: string;
    inputText: string;
    outputText: string;
    costText?: string
  } | null {
    // KI-Modell/Token/Kosten sind vertrauliche Kostendaten - nur für den plattform-globalen Admin
    // sichtbar, nicht für Organisations-/Projekt-Admins, -Bearbeiter oder -Beobachter.
    if (!this.permissionService.hasGlobalPrivilege('GLOBAL_CHECKRULE_ADMIN')) return null;

    let inputSum = 0;
    let outputSum = 0;
    let hasUsage = false;
    const models = new Set<string>();
    // Tokens je Modell, um pro Modell mit dem passenden Preis zu rechnen (ein Check kann
    // mehrere KI-Modelle durchlaufen, z.B. bei gemischten Regelsets).
    const tokensByModel = new Map<string, { input: number; output: number }>();

    for (const log of this.logs) {
      const details = log.details;
      if (!details) continue;
      const inputTokens = details['inputTokens'];
      const outputTokens = details['outputTokens'];
      if (inputTokens == null && outputTokens == null) continue;

      hasUsage = true;
      const inputNum = typeof inputTokens === 'number' ? inputTokens : Number(inputTokens);
      const outputNum = typeof outputTokens === 'number' ? outputTokens : Number(outputTokens);
      const model = details['model'] ? String(details['model']) : undefined;
      if (Number.isFinite(inputNum)) inputSum += inputNum;
      if (Number.isFinite(outputNum)) outputSum += outputNum;
      if (model) models.add(model);

      if (model) {
        const entry = tokensByModel.get(model) ?? {input: 0, output: 0};
        if (Number.isFinite(inputNum)) entry.input += inputNum;
        if (Number.isFinite(outputNum)) entry.output += outputNum;
        tokensByModel.set(model, entry);
      }
    }

    if (!hasUsage) return null;

    return {
      models: Array.from(models).join(', '),
      inputText: this.formatTokenCount(inputSum),
      outputText: this.formatTokenCount(outputSum),
      costText: this.formatCost(this.estimateCostUsd(tokensByModel)),
    };
  }

  /**
   * Ob eine Zeile Details hat – ohne sie zu bauen.
   *
   * Für Einträge aus dem Protokoll-Endpunkt entscheidet das Backend ({@code hasDetails}); für
   * Einträge, die live über SSE hereinkommen, gibt es kein StepLog zum Nachladen, dort zählt das
   * mitgelieferte `details`-Objekt. Die Prüfung ist in beiden Fällen O(1) und rührt keinen Wert an.
   */
  private rowHasDetails(log: LogEntry): boolean {
    if (!this.canViewLogDetails()) return false;
    if (log.hasDetails != null) return log.hasDetails;
    return !!log.details && Object.keys(log.details).length > 0;
  }

  private canViewLogDetails(): boolean {
    return this.permissionService.hasGlobalPrivilege('GLOBAL_CHECKRULE_ADMIN');
  }

  private buildInlineDetailRows(log: LogEntry): LogDetailRow[] {
    if (!log.details) return [];
    return Object.entries(log.details)
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => truncateDetailValue(key, this.stringifyDetailValue(value)));
  }

  private toFiniteNumber(value: unknown): number | null {
    if (value === null || value === undefined) return null;
    const num = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(num) ? num : null;
  }

  /** Formatiert Tokenzahlen kompakt (z.B. 552.608 -> "552,6k"), damit das Badge nicht zu breit wird. */
  private formatTokenCount(value: unknown): string {
    const num = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(num)) return '–';
    if (Math.abs(num) >= 1000) {
      return `${(num / 1000).toLocaleString('de-DE', {maximumFractionDigits: 1})}k`;
    }
    return num.toLocaleString('de-DE');
  }

  formatRelativeTimestamp(log: LogEntry): string {
    if (!log.occurredAtIso || !this.progress.startedAtIso) {
      return log.timestamp || '–';
    }

    const startedAt = new Date(this.progress.startedAtIso).getTime();
    const occurredAt = new Date(log.occurredAtIso).getTime();
    if (Number.isNaN(startedAt) || Number.isNaN(occurredAt)) {
      return log.timestamp || '–';
    }

    const elapsedSeconds = Math.max(0, (occurredAt - startedAt) / 1000);
    const minutes = Math.floor(elapsedSeconds / 60);
    const seconds = elapsedSeconds % 60;
    return `+${String(minutes).padStart(2, '0')}:${seconds.toFixed(1).padStart(4, '0')}`;
  }

  /**
   * Absolute Uhrzeit eines Log-Eintrags für den Tooltip auf der Zeit-Spalte im Ereignisprotokoll.
   * Die Zeile zeigt nur die relative Zeit (+MM:SS.s); der Tooltip liefert Datum und Uhrzeit.
   */
  formatAbsoluteTimestamp(log: LogEntry): string {
    if (log.occurredAtIso) {
      const value = this.toDateTimeDisplay(log.occurredAtIso);
      if (value) return value;
    }
    return log.timestamp || '–';
  }

  ruleSetBadgeType(type: string): 'success' | 'warning' | 'error' | 'info' {
    switch (type?.toUpperCase()) {
      case 'HYBRID':
        return 'info';
      case 'KI':
      case 'AI':
        return 'info';
      default:
        return 'info';
    }
  }

  ruleSetBadgeLabel(type: string): string {
    switch (type?.toUpperCase()) {
      case 'HYBRID':
        return 'Hybride Prüfung';
      case 'KI':
      case 'AI':
        return 'KI Prüfung';
      default:
        return 'IDS Prüfung';
    }
  }

  /**
   * Mit expliziten Einheiten statt mm:ss ("3:56"), das ohne Kontext nicht erkennen lässt, ob
   * Minuten oder Stunden gemeint sind - hier schwankt die Größenordnung je Regel/Datei stärker
   * als bei der laufenden Gesamtdauer (step-processing.component.ts), wo mm:ss eindeutig genug ist.
   */
  private formatDurationMs(ms?: number): string {
    if (ms == null || !Number.isFinite(ms) || ms < 0) return '–';
    const totalSeconds = Math.round(ms / 1000);
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const totalMinutes = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    if (totalMinutes < 60) return `${totalMinutes}m ${secs}s`;
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return `${hours}h ${mins}m`;
  }

  private formatBytes(bytes: number): string {
    if (!bytes || bytes === 0) return '–';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }

  private normalizeLogLevel(level: string): 'INFO' | 'WARNING' | 'ERROR' {
    const normalized = (level || '').toUpperCase();
    if (normalized === 'ERROR') return 'ERROR';
    if (normalized === 'WARN' || normalized === 'WARNING') return 'WARNING';
    return 'INFO';
  }

  private stringifyDetailValue(value: unknown): string {
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  private toDateTimeDisplay(value: string): string | undefined {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;
    const datePart = date.toLocaleDateString('de-DE');
    const timePart = date.toLocaleTimeString('de-DE');
    return `${datePart} · ${timePart}`;
  }
}
